import apiClient from '@/api/client';

export interface Company {
  id: string;
  name: string;
  short_code: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  lga?: string;
  state?: string;
  postal_code?: string;
  email?: string;
  phone?: string;
  website?: string;
  description?: string;
  industry?: string;
  registration_number?: string;
  colour: string;
  logo_url?: string;
  financial_year_start: number;
  base_currency: string;
  country_code: string;
  timezone?: string;
  is_active: boolean;
  // Social media
  linkedin_url?: string;
  twitter_url?: string;
  facebook_url?: string;
  instagram_url?: string;
  youtube_url?: string;
  // Display preferences
  date_format?: string;
  time_format?: string;
  // NRS e-invoicing
  tin?: string | null;
  // Console-managed theme (tenant self-service override). Persisted into
  // companies.settings.theme on the backend; resolved + surfaced on /auth/me.
  theme?: ThemeConfig;
}

export interface ThemeConfig {
  palette: string | null;
  style: 'minimal' | 'balanced' | 'soft';
  dark_default: boolean;
}

export interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone?: string;
  is_active: boolean;
  // is_system_admin removed in Sprint 4b.
}

export interface Role {
  id: string;
  name: string;
  description?: string;
  is_system: boolean;
}

export interface Invitation {
  id: string;
  email: string;
  first_name: string | null;
  last_name: string | null;
  company_id: string;
  role_id: string;
  status: string; // pending | accepted | expired | revoked
  expires_at: string;
  created_at: string;
}

export interface Permission {
  id: string;
  code: string;
  module: string;
  description?: string;
}

export interface ReferenceData {
  id: string;
  category: string;
  code: string;
  name: string;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  metadata_?: Record<string, any>;
}

// Jurisdiction types
export interface SupportedCountry {
  code: string;
  name: string;
  currency: string;
  timezone: string;
}

export interface JurisdictionDefaults {
  country_code: string;
  country_name: string;
  currency_code: string;
  timezone: string;
  fiscal_year_start: number;
  minimum_wage: number;
  statutory_deductions: { code: string; name: string; is_employer: boolean; default_rate: number }[];
  leave_minimums: { leave_type: string; min_days: number; is_paid: boolean }[];
  working_hours: { daily_hours: number; weekly_hours: number; rest_days_per_week: number };
  probation_rules: { default_months: number; max_months: number; notice_days_during_probation: number };
  reference_data: { category: string; code: string; name: string; sort_order: number }[];
  statutory_accounts: Record<string, string>;
  filing_calendar: { code: string; name: string; frequency: string; due_description: string }[];
  supported_countries: string[];
  vat_rate?: number;
  wht_rate_professional?: number;
  wht_rate_director?: number;
}

export const settingsApi = {
  // Company
  getCurrentCompany: () => apiClient.get<Company>('/settings/companies/current'),
  updateCompany: (companyId: string, data: Partial<Company>) =>
    apiClient.patch<Company>(`/settings/companies/${companyId}`, data),
  uploadCompanyLogo: (companyId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return apiClient.post<{ logo_url: string }>(`/settings/companies/${companyId}/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Default Homepage
  getDefaultHomepage: (companyId: string) =>
    apiClient.get<{ default_homepage: string; default_module: string | null }>(
      `/settings/companies/${companyId}/default-homepage`
    ),
  updateDefaultHomepage: (companyId: string, data: { default_homepage: string; default_module?: string }) =>
    apiClient.patch<{ default_homepage: string; default_module: string | null }>(
      `/settings/companies/${companyId}/default-homepage`, data
    ),

  // Users
  listUsers: () => apiClient.get<User[]>('/settings/users'),
  createUser: (data: {
    email: string;
    first_name: string;
    last_name: string;
    password: string;
    phone?: string;
    role_id: string;
  }) => apiClient.post<User>('/settings/users', data),

  // Invite a user by email — they set their own password via the accept link.
  // Preferred over createUser for new accounts: admin never sees the password.
  inviteUser: (data: {
    email: string;
    role_name: string;
    first_name?: string;
    last_name?: string;
    // Optional target company. Defaults to the active company server-side;
    // a multi-company admin may invite into another company they manage.
    company_id?: string;
  }) => apiClient.post('/auth/invitations', data),

  // Invitation lifecycle — list (with status filter), revoke, resend.
  listInvitations: (status?: string) =>
    apiClient.get<Invitation[]>('/auth/invitations', {
      params: status ? { status } : undefined,
    }),
  revokeInvitation: (invitationId: string) =>
    apiClient.delete(`/auth/invitations/${invitationId}`),
  resendInvitation: (invitationId: string) =>
    apiClient.post<Invitation>(`/auth/invitations/${invitationId}/resend`),

  updateUser: (userId: string, data: Partial<User>) =>
    apiClient.patch<User>(`/settings/users/${userId}`, data),

  // Roles
  listRoles: () => apiClient.get<Role[]>('/settings/roles'),

  // Permissions
  listPermissions: () => apiClient.get<Permission[]>('/settings/permissions'),
  getRolePermissions: (roleId: string) => apiClient.get<string[]>(`/settings/roles/${roleId}/permissions`),
  getAllRolePermissions: () => apiClient.get<Record<string, string[]>>('/settings/roles/all-permissions'),
  // Permission-SAVE methods removed (RBAC hardening 2026-06-29): the weak
  // /settings/roles permission-write endpoints were deleted server-side.
  // Use adminRolesApi.replacePermissions / adminRolesApi.bulkAssignPermissions
  // (hardened: escalation boundary + SoD + audit).

  // Reference Data
  listReferenceData: (category?: string) =>
    apiClient.get<ReferenceData[]>('/settings/reference-data', {
      params: category ? { category } : {},
    }),
  createReferenceData: (data: {
    category: string;
    code: string;
    name: string;
    sort_order?: number;
    metadata_?: Record<string, any>;
  }) => apiClient.post<ReferenceData>('/settings/reference-data', data),
  updateReferenceData: (id: string, data: Partial<ReferenceData>) =>
    apiClient.patch<ReferenceData>(`/settings/reference-data/${id}`, data),
  deleteReferenceData: (id: string) =>
    apiClient.delete<void>(`/settings/reference-data/${id}`),

  // Locations
  listLocations: () =>
    apiClient.get<{ id: string; name: string; company_id: string; address_line_1?: string; address_line_2?: string; city?: string; lga?: string; state?: string; postal_code?: string; country?: string; is_active: boolean }[]>(
      '/settings/locations'
    ),
  locations: () => apiClient.get('/settings/locations'),
  createLocation: (data: { name: string; address_line_1?: string; address_line_2?: string; city?: string; lga?: string; state?: string; postal_code?: string; country?: string }) =>
    apiClient.post('/settings/locations', data),
  updateLocation: (id: string, data: { name?: string; address_line_1?: string; address_line_2?: string; city?: string; lga?: string; state?: string; postal_code?: string; country?: string }) =>
    apiClient.patch(`/settings/locations/${id}`, data),
  deleteLocation: (id: string) =>
    apiClient.delete<{ detail: string }>(`/settings/locations/${id}`),
  buildings: (locationId?: string) =>
    apiClient.get('/settings/buildings', {
      params: locationId ? { location_id: locationId } : {},
    }),
  createBuilding: (data: { location_id: string; name: string }) =>
    apiClient.post('/settings/buildings', data),
  updateBuilding: (id: string, data: { name?: string }) =>
    apiClient.patch(`/settings/buildings/${id}`, data),
  deleteBuilding: (id: string) =>
    apiClient.delete<{ detail: string }>(`/settings/buildings/${id}`),
  units: (buildingId?: string) =>
    apiClient.get('/settings/units', {
      params: buildingId ? { building_id: buildingId } : {},
    }),
  createUnit: (data: { building_id: string; name: string; unit_type?: string; description?: string; occupancy_type?: string }) =>
    apiClient.post('/settings/units', data),
  updateUnit: (id: string, data: { name?: string; unit_type?: string; description?: string; occupancy_type?: string }) =>
    apiClient.patch(`/settings/units/${id}`, data),
  deleteUnit: (id: string) =>
    apiClient.delete<{ detail: string }>(`/settings/units/${id}`),

  // Import Templates
  downloadImportTemplate: (importType: string) =>
    apiClient.get(`/settings/import/template/${importType}`, { responseType: 'blob' }),

  // Data Import (CSV/XLSX)
  importChartOfAccounts: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>('/settings/import/chart-of-accounts', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importEmployees: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>('/settings/import/employees', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importBudgets: (file: File, year: number) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(`/settings/import/budgets?year=${year}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importJournalEntries: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>('/settings/import/journal-entries', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importLeaveBalances: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>('/settings/import/leave-balances', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importTransactions: (file: File, kind: 'revenue' | 'expense') => {
    const formData = new FormData();
    formData.append('file', file);
    return apiClient.post<ImportResult>(`/settings/import/transactions/${kind}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importProperties: (file: File, options?: PropertyImportOptions) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (options?.skip_existing !== undefined) params.set('skip_existing', String(options.skip_existing));
    const qs = params.toString();
    return apiClient.post<ImportResult>(`/settings/import/properties${qs ? `?${qs}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importTenants: (file: File, options?: TenantImportOptions) => {
    const formData = new FormData();
    formData.append('file', file);
    const params = new URLSearchParams();
    if (options?.skip_existing !== undefined) params.set('skip_existing', String(options.skip_existing));
    if (options?.update_existing !== undefined) params.set('update_existing', String(options.update_existing));
    if (options?.generate_receivables !== undefined) params.set('generate_receivables', String(options.generate_receivables));
    if (options?.auto_create_units !== undefined) params.set('auto_create_units', String(options.auto_create_units));
    const qs = params.toString();
    return apiClient.post<ImportResult>(`/settings/import/tenants${qs ? `?${qs}` : ''}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Jurisdiction / Country defaults
  getJurisdictionDefaults: (countryCode: string) =>
    apiClient.get<JurisdictionDefaults>(`/settings/jurisdiction-defaults/${countryCode}`),

  getJurisdictionPreview: (countryCode: string) =>
    apiClient.get(`/settings/jurisdiction-preview/${countryCode}`),

  // Country-specific UI metadata (field format hints today; reserved for
  // more sections like bank_holidays later). Cached client-side for 24h.
  getCountryMetadata: (countryCode: string, sections: string[] = ['formats']) =>
    apiClient.get<CountryMetadata>(`/settings/country-metadata/${countryCode}`, {
      params: { sections: sections.join(',') },
    }),

  getSupportedCountries: () =>
    apiClient.get<SupportedCountry[]>('/settings/supported-countries'),

  // Holiday templates & seeding
  getHolidayTemplate: (countryCode: string, year: number) =>
    apiClient.get<HolidayTemplateResponse>(`/settings/holidays/template/${countryCode}?year=${year}`),

  seedHolidays: (data: { country_code: string; year: number; selected_holidays: string[] }) =>
    apiClient.post('/settings/holidays/seed', data),

  // Company holiday profile
  getCompanyHolidays: () =>
    apiClient.get<CompanyHolidaysResponse>('/settings/holidays/company'),

  updateCompanyHolidays: (data: { holidays: CompanyHolidayToggle[] }) =>
    apiClient.put<CompanyHolidaysResponse>('/settings/holidays/company', data),

  // Dynamic Import (preview-first)
  dynamicImportPreview: (file: File, entityType?: string, manualMappings?: Record<string, string>) => {
    const formData = new FormData();
    formData.append('file', file);
    const queryParts: string[] = [];
    if (entityType) queryParts.push(`entity_type=${encodeURIComponent(entityType)}`);
    if (manualMappings && Object.keys(manualMappings).length > 0) {
      queryParts.push(`manual_mappings=${encodeURIComponent(JSON.stringify(manualMappings))}`);
    }
    const params = queryParts.length > 0 ? `?${queryParts.join('&')}` : '';
    return apiClient.post<DynamicPreviewResult>(`/settings/import/dynamic/preview${params}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  dynamicImportCommit: (data: DynamicCommitRequest) =>
    apiClient.post<DynamicCommitResult>('/settings/import/dynamic/commit', data),

  getImportHistory: (limit = 20) =>
    apiClient.get<ImportHistoryEntry[]>(`/settings/import/history?limit=${limit}`),

  // Phase 9 — Reload country profile pack
  reloadCountryProfile: () =>
    apiClient.post('/settings/statutory-rates/reload-profile'),
};

// ── Country metadata (UI hints from profile packs) ─────────────────────
export interface FieldFormat {
  label?: string;
  placeholder?: string;
  helper_text?: string;
  regex?: string;
  example_values?: string[];
}

export interface CountryMetadata {
  country_code: string;
  version: string | null;
  field_formats?: Record<string, FieldFormat>;
}

export interface HolidayTemplateItem {
  name: string;
  is_moveable: boolean;
  typical_month: number;
  typical_day: number;
  date: string;
  description?: string;
}

export interface HolidayTemplateResponse {
  country_code: string;
  country_name: string;
  year: number;
  holidays: HolidayTemplateItem[];
}

// Company Holiday Profile
export interface CompanyHolidayItem {
  group_public_holiday_id: string;
  holiday_name: string;
  observed_date: string;
  is_moveable: boolean;
  group_is_full_day: boolean;
  group_is_active: boolean;
  is_observed: boolean;
  is_full_day: boolean;
}

export interface CompanyHolidaysResponse {
  items: CompanyHolidayItem[];
  total: number;
}

export interface CompanyHolidayToggle {
  group_public_holiday_id: string;
  is_observed: boolean;
  is_full_day: boolean;
}

// Notification Templates
export interface NotificationTemplate {
  id: string;
  group_id: string;
  key: string;
  language: string;
  category: string;
  title: string;
  body: string;
  description?: string;
}

export interface NotificationTemplateList {
  items: NotificationTemplate[];
  total: number;
  category?: string;
}

export interface NotificationTemplateCreate {
  key: string;
  category: string;
  title: string;
  body: string;
  description?: string;
}

export interface NotificationTemplateUpdate {
  title: string;
  body: string;
  description?: string;
}

export const notificationTemplateApi = {
  list: (params?: { category?: string; language?: string }) =>
    apiClient.get<NotificationTemplateList>('/settings/notification-templates', { params }),

  get: (key: string, language?: string) =>
    apiClient.get<NotificationTemplate>(`/settings/notification-templates/${key}`, {
      params: language ? { language } : {},
    }),

  create: (data: NotificationTemplateCreate, language?: string) =>
    apiClient.post<NotificationTemplate>('/settings/notification-templates', data, {
      params: language ? { language } : {},
    }),

  update: (key: string, data: NotificationTemplateUpdate, language?: string) =>
    apiClient.put<NotificationTemplate>(`/settings/notification-templates/${key}`, data, {
      params: language ? { language } : {},
    }),

  delete: (key: string, language?: string) =>
    apiClient.delete(`/settings/notification-templates/${key}`, {
      params: language ? { language } : {},
    }),

  reset: (key: string, language?: string) =>
    apiClient.post<NotificationTemplate>(`/settings/notification-templates/${key}/reset`, null, {
      params: language ? { language } : {},
    }),
};

export interface ImportResult {
  imported: number;
  skipped?: number;
  updated?: number;
  leases_created?: number;
  errors: string[];
}

export interface TenantImportOptions {
  skip_existing?: boolean;
  update_existing?: boolean;
  generate_receivables?: boolean;
  auto_create_units?: boolean;
}

export interface PropertyImportOptions {
  skip_existing?: boolean;
}

// Dynamic Import (preview-first)
export interface ValidationSummaryItem {
  level: 'error' | 'warning' | 'info';
  message: string;
  count: number;
}

export interface DynamicPreviewResult {
  entity_type: string;
  entity_label: string;
  detected: boolean;
  columns: string[];
  required_columns: string[];
  missing_columns: string[];
  file_headers: string[];
  column_mappings: Record<string, string>; // {original_header: canonical_name}
  unmatched_headers: string[];  // headers that couldn't be auto-mapped
  available_columns: string[];  // canonical columns for manual mapping UI
  rows: Record<string, any>[];
  errors: Record<string, Record<string, string>>; // rowIndex -> {col: errorMsg}
  warnings: Record<string, Record<string, string>>; // rowIndex -> {col: warningMsg}
  validation_summary: ValidationSummaryItem[];
  summary: { total: number; valid: number; invalid: number; warnings?: number };
}

export interface DynamicCommitRequest {
  entity_type: string;
  rows: Record<string, any>[];
  selected_indices?: number[];
  options?: Record<string, any>;
}

export interface DynamicCommitResult {
  imported: number;
  skipped?: number;
  updated?: number;
  leases_created?: number;
  errors: string[];
}

export interface ImportHistoryEntry {
  id: string;
  entity_type: string;
  entity_label: string;
  file_name: string | null;
  total_rows: number;
  imported: number;
  updated: number;
  skipped: number;
  errors_count: number;
  errors: string[];
  user_name: string;
  created_at: string;
}

// Bank Setup
export interface Bank {
  id: string;
  name: string;
  code: string;
  country_code: string | null;
  sort_order: number;
  is_active: boolean;
  is_system: boolean;
  logo_url: string | null;
}

export const bankApi = {
  list: (countryCode?: string) =>
    apiClient.get<Bank[]>('/settings/banks', {
      params: countryCode ? { country_code: countryCode } : {},
    }),
  create: (data: { name: string; code: string; country_code?: string; sort_order?: number }) =>
    apiClient.post<Bank>('/settings/banks', data),
  update: (id: string, data: Partial<Bank>) =>
    apiClient.patch<Bank>(`/settings/banks/${id}`, data),
  delete: (id: string) =>
    apiClient.delete(`/settings/banks/${id}`),
  downloadTemplate: () =>
    apiClient.get('/settings/import/template/banks', { responseType: 'blob' }),
};

// Module Configuration
export interface ModuleConfig {
  key: string;
  name: string;
  description?: string;
  icon?: string;
  is_core: boolean;
  is_enabled: boolean;
  sort_order: number;
}

export interface ModuleTemplate {
  id: string;
  key: string;
  name: string;
  description?: string;
  enabled_modules: string[];
}

export interface ModuleDiff {
  enabled: string[];
  disabled: string[];
}

export interface ModuleUpdateResponse {
  config: ModuleConfig[];
  diff: ModuleDiff;
}

export interface ApplyTemplateResponse {
  config: ModuleConfig[];
  diff: ModuleDiff;
  plan_blocked: string[];
  applied_template_key: string | null;
}

export interface TemplatePreview {
  will_enable: string[];
  will_disable: string[];
  plan_blocked: string[];
  unchanged: string[];
}

export interface AppliedTemplate {
  key: string | null;
  name: string | null;
}

export const moduleApi = {
  getModuleConfig: () =>
    apiClient.get<ModuleConfig[]>('/settings/modules'),

  updateModuleConfig: (modules: { key: string; enabled: boolean }[]) =>
    apiClient.put<ModuleUpdateResponse>('/settings/modules', { modules }),

  getTemplates: () =>
    apiClient.get<ModuleTemplate[]>('/settings/modules/templates'),

  previewTemplate: (templateId: string) =>
    apiClient.get<TemplatePreview>(`/settings/modules/templates/${templateId}/preview`),

  applyTemplate: (templateId: string) =>
    apiClient.post<ApplyTemplateResponse>('/settings/modules/apply-template', {
      template_id: templateId,
    }),

  getAppliedTemplate: () =>
    apiClient.get<AppliedTemplate>('/settings/modules/applied-template'),
};

// ── Group Defaults + Company Overrides ──

export interface GroupSettingItem {
  id: string;
  module: string;
  setting_key: string;
  setting_value: any;
  description?: string;
  label?: string;
  type?: string;
}

export interface ResolvedSetting {
  key: string;
  value: any;
  group_value: any;
  source: 'company' | 'group' | 'system';
  group_setting_id: string | null;
  override_id: string | null;
  label: string;
  type: string;
}

export interface CompanyOverrideStatus {
  company_id: string;
  company_name: string;
  status: 'inherited' | 'customized';
  overrides_count: number;
  overrides: { key: string; value: any; group_value: any }[];
}

export const groupDefaultsApi = {
  // Group admin — list defaults for a module
  listDefaults: (module: string) =>
    apiClient.get<GroupSettingItem[]>('/settings/group-defaults', { params: { module } }),

  // Group admin — bulk upsert defaults
  saveDefaults: (module: string, settings: { setting_key: string; setting_value: any; description?: string }[]) =>
    apiClient.put('/settings/group-defaults', { module, settings }),

  // Group admin — per-company override status
  getCompanyStatus: (module: string) =>
    apiClient.get<CompanyOverrideStatus[]>('/settings/group-defaults/companies', { params: { module } }),

  // Group admin — resolved settings for a specific company
  getCompanyResolved: (companyId: string, module: string) =>
    apiClient.get<ResolvedSetting[]>(`/settings/group-defaults/companies/${companyId}/resolved`, { params: { module } }),

  // Group admin — set overrides for a company
  setCompanyOverrides: (companyId: string, settings: { group_setting_id: string; setting_value: any }[]) =>
    apiClient.put(`/settings/group-defaults/companies/${companyId}`, { settings }),

  // Group admin — reset a company override
  resetCompanyOverride: (companyId: string, groupSettingId: string) =>
    apiClient.delete(`/settings/group-defaults/companies/${companyId}/${groupSettingId}`),

  // Company user — read-only view of resolved settings (no write access)
  getMyCompanySettings: (module: string) =>
    apiClient.get<ResolvedSetting[]>('/settings/group-defaults/my-company', { params: { module } }),

  // Cross-module dashboard — override counts per company per module
  getDashboard: () =>
    apiClient.get<{ company_id: string; company_name: string; colour: string; accounting: number; hr: number; payroll: number; communication: number; total: number }[]>('/settings/group-defaults/dashboard'),

  // Settings registry
  getRegistry: (module?: string) =>
    apiClient.get('/settings/group-defaults/registry', { params: module ? { module } : {} }),
};
